import { useState } from "react";
import axios from "axios";
import { FileText, Download, CheckCircle, XCircle, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceView = ({ invoice, onClose, onUpdate, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(invoice);
  const token = localStorage.getItem("token");

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const togglePaymentStatus = async () => {
    const newStatus = currentInvoice.payment_status === "PAID" ? "UNPAID" : "PAID";
    setLoading(true);
    try {
      const res = await axios.put(
        `${API}/invoices/${currentInvoice.id}/payment-status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentInvoice(res.data);
      if (onUpdate) onUpdate(res.data);
      toast.success(`Invoice marked as ${newStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update payment status");
    } finally {
      setLoading(false);
    }
  };

  if (!currentInvoice) return null;

  const isPaid = currentInvoice.payment_status === "PAID";

  return (
    <div className="bg-white" data-testid="invoice-view">
      {/* Invoice Header */}
      <div className="bg-slate-900 text-white p-6 rounded-t-lg print:rounded-none">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-chivo font-bold text-amber-500">SHIPP SMART</h1>
            <p className="text-slate-400 text-sm mt-1">Shipping & Logistics</p>
          </div>
          <div className="text-right">
            <p className="text-amber-500 font-mono font-bold text-lg">{currentInvoice.invoice_number}</p>
            <p className="text-slate-400 text-sm">{formatDate(currentInvoice.date)}</p>
          </div>
        </div>
      </div>

      {/* Payment Status Banner */}
      <div className={`p-4 flex items-center justify-between ${isPaid ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}`}>
        <div className="flex items-center gap-3">
          {isPaid ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : (
            <XCircle className="w-6 h-6 text-red-500" />
          )}
          <div>
            <Badge className={`${isPaid ? 'bg-green-500' : 'bg-red-500'} text-white text-sm px-3 py-1`}>
              {isPaid ? 'PAID' : 'UNPAID'}
            </Badge>
            {isPaid && currentInvoice.payment_date && (
              <p className="text-green-600 text-xs mt-1">Paid on {formatDate(currentInvoice.payment_date)}</p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={togglePaymentStatus}
          disabled={loading}
          className={`print:hidden ${isPaid ? 'text-red-600 hover:bg-red-50 border-red-300' : 'text-green-600 hover:bg-green-50 border-green-300'}`}
          data-testid="toggle-payment-btn"
        >
          {loading ? "..." : isPaid ? "Mark as Unpaid" : "Mark as Paid"}
        </Button>
      </div>

      {/* Bill To */}
      <div className="p-6 border-b border-slate-200">
        <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Bill To</p>
        <p className="font-bold text-slate-900 text-lg">{currentInvoice.customer_name}</p>
      </div>

      {/* Items Table */}
      <div className="p-6">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-200">
              <th className="text-left text-xs uppercase tracking-widest text-slate-500 pb-3">Description</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 pb-3">Weight</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 pb-3">Volume</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 pb-3">Price</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 pb-3">Fixed Fee</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 pb-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {currentInvoice.items.map((item, index) => (
              <tr key={index} className="border-b border-slate-100">
                <td className="py-4 text-slate-900">{item.description}</td>
                <td className="py-4 text-right text-slate-600 font-mono text-sm">
                  {item.weight ? `${item.weight} lbs` : '-'}
                </td>
                <td className="py-4 text-right text-slate-600 font-mono text-sm">
                  {item.volume ? `${item.volume} ft³` : '-'}
                </td>
                <td className="py-4 text-right text-slate-600 font-mono text-sm">
                  ${(item.price || 0).toFixed(2)}
                </td>
                <td className="py-4 text-right text-slate-600 font-mono text-sm">
                  ${(item.fixed_fee || 0).toFixed(2)}
                </td>
                <td className="py-4 text-right font-mono font-bold text-slate-900">
                  ${(item.total || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-slate-50 p-6 print:rounded-none">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal:</span>
            <span className="font-mono">${(currentInvoice.subtotal || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-600">
            <span>Shipping Fee:</span>
            <span className="font-mono">${(currentInvoice.shipping_fee || 0).toFixed(2)}</span>
          </div>
          {currentInvoice.discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount:</span>
              <span className="font-mono">-${(currentInvoice.discount || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-xl font-bold text-slate-900 border-t border-slate-300 pt-3 mt-3">
            <span>Grand Total:</span>
            <span className="font-mono text-amber-600">${(currentInvoice.grand_total || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {currentInvoice.notes && (
        <div className="p-6 border-t border-slate-200">
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Notes</p>
          <p className="text-slate-600">{currentInvoice.notes}</p>
        </div>
      )}

      {/* Actions (hidden on print) */}
      <div className="p-6 border-t border-slate-200 flex gap-3 print:hidden">
        {onEdit && (
          <Button
            variant="outline"
            onClick={() => onEdit(currentInvoice)}
            className="text-blue-600 hover:bg-blue-50 border-blue-300"
            data-testid="edit-invoice-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
        <Button
          onClick={handlePrint}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
        >
          <Download className="w-4 h-4 mr-2" />
          Print / Download
        </Button>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
};

export default InvoiceView;
